// Rolling check-in tasks (tasks only: nothing here sends an email or text).
// Pipeline buyer records: "Check in with {name}" at +48 hours from creation, then creation +7 days,
// then every 14 days after that, one open task at a time, until the agent stops them (checkInsStopped),
// or the record is closed, archived, merged or converted to a transaction.
// Deals: once Under Contract / Pending, "Weekly check-in with {client}" every 7 days until closed,
// cancelled, terminated (or lost / withdrawn / expired).
const DAY = 24 * 60 * 60 * 1000;
const PIPELINE_SOURCE = 'pipeline-checkin';
const DEAL_SOURCE = 'deal-checkin';
const DONE_STATUSES = ['completed', 'na'];
const PIPELINE_STOP_STATUSES = ['closed', 'archived', 'converted', 'lost', 'dead', 'not interested'];
const DEAL_STOP_STATUSES = ['closed', 'cancelled', 'canceled', 'terminated', 'lost', 'withdrawn', 'expired'];

const etParts = (d) => {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(d).map((x) => [x.type, x.value]));
  return { day: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
};

// Due date of check-in number n (1-based) for a record created at createdAt. If that is already past
// (the previous one was finished late), it rolls forward in 14-day steps so the next one is never overdue on arrival.
function pipelineCheckInDue(createdAt, n, now = new Date()) {
  const start = new Date(createdAt || now).getTime();
  const offset = n <= 1 ? 2 * DAY : n === 2 ? 7 * DAY : 7 * DAY + 14 * DAY * (n - 2);
  let due = start + offset;
  if (n > 1) while (due <= now.getTime()) due += 14 * DAY;
  return new Date(due);
}

const lc = (v) => String(v || '').trim().toLowerCase();
const isConverted = (r) => !!(r && (r.convertedTransactionId || r.transactionId || Object.keys(r.convertedTransactions || {}).length));

function pipelineCheckInsActive(r) {
  if (!r || r.checkInsStopped || r.archived || r.deleted || r.mergedInto || isConverted(r)) return false;
  return ![r.status, r.pipelineStatus].some((s) => PIPELINE_STOP_STATUSES.includes(lc(s)));
}

function dealCheckInsActive(t) {
  if (!t || t.checkInsStopped || t.actualClosingDate || t.archived || t.deleted) return false;
  return !DEAL_STOP_STATUSES.includes(lc(t.status));
}

const isUnderContract = (t) => !!(t && (t.underContract || t.underContractDate || lc(t.status) === 'pending' || lc(t.status) === 'under contract'));

const recordName = (r) => String(r?.name || [r?.firstName, r?.lastName].filter(Boolean).join(' ') || r?.clientName || '').trim();

function pipelineCheckInTask(r, id, n, now = new Date()) {
  const due = pipelineCheckInDue(r.createdAt, n, now);
  const { day, time } = etParts(due);
  const label = recordName(r) || 'buyer';
  return {
    name: `Check in with ${label}`, title: `Check in with ${label}`,
    description: `Pipeline check-in #${n}. Next one is created when you mark this done or N/A. Stop them with "Stop check-ins" on the pipeline record.`,
    type: 'Follow-up', source: PIPELINE_SOURCE, buyerInterestId: id, checkInNumber: n,
    dueDate: day, dueTime: time, dueAt: due.toISOString(),
  };
}

function dealCheckInTask(t, txId, ownerUid, n, now = new Date()) {
  const due = new Date(now.getTime() + 7 * DAY);
  const { day, time } = etParts(due);
  const label = recordName(t) || t?.propertyAddress || 'client';
  return {
    name: `Weekly check-in with ${label}`, title: `Weekly check-in with ${label}`,
    description: `Under contract${t?.propertyAddress ? `: ${t.propertyAddress}` : ''}. Weekly check-in #${n}. The next one is created when you mark this done or N/A, until the deal is closed, cancelled or terminated.`,
    type: 'Follow-up', source: DEAL_SOURCE, dealId: txId, dealOwnerUid: ownerUid || null, checkInNumber: n,
    dueDate: day, dueTime: time, dueAt: due.toISOString(),
  };
}

const isCheckInTask = (task) => !!task && (task.source === PIPELINE_SOURCE || task.source === DEAL_SOURCE);
const finishesCheckIn = (task, newStatus) => isCheckInTask(task) && DONE_STATUSES.includes(newStatus) && !task.nextCheckInTaskId;

module.exports = {
  PIPELINE_SOURCE, DEAL_SOURCE, pipelineCheckInDue, pipelineCheckInsActive, dealCheckInsActive, isUnderContract,
  pipelineCheckInTask, dealCheckInTask, isCheckInTask, finishesCheckIn, etParts,
};
